// Decorative glyphs for the Sparkle theme — inline SVG, exact paths from the
// design handoff prototype (sparkle-mode.jsx). Keep small (12–24px) and scatter
// sparingly; legibility first. Also usable as metric icons / title bullets.
import type { CSSProperties } from "react";

interface GlyphProps {
  size?: number;
  color?: string;
  className?: string;
  style?: CSSProperties;
}

/** Four-point star burst. */
export function Sparkle({ size = 18, color = "#FFD400", className, style }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
      <path
        d="M12 0c1 6.5 5.5 11 12 12-6.5 1-11 5.5-12 12-1-6.5-5.5-11-12-12C6.5 11 11 6.5 12 0Z"
        fill={color}
      />
    </svg>
  );
}

/** Five-point star with a white keyline. */
export function Star5({ size = 18, color = "#FF2D9B", className, style }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
      <path
        d="M12 1.6 15 8.5l7.4.6-5.6 4.9 1.7 7.2L12 17.6 5.5 21.2l1.7-7.2L1.6 9.1 9 8.5 12 1.6Z"
        fill={color}
        stroke="#fff"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Heart with a white keyline. */
export function Heart({ size = 18, color = "#E81FA8", className, style }: GlyphProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
      <path
        d="M12 21s-8-5.1-8-11A4.6 4.6 0 0 1 12 6a4.6 4.6 0 0 1 8 4c0 5.9-8 11-8 11Z"
        fill={color}
        stroke="#fff"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Layered rainbow arc. */
export function Rainbow({
  width = 70,
  className,
  style,
}: {
  width?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const bands = ["#FF2D9B", "#FF861E", "#FFD400", "#10E0B4", "#2D7BFF"];
  return (
    <svg
      width={width}
      height={width * 0.6}
      viewBox="0 0 100 60"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {bands.map((c, i) => (
        <path
          key={i}
          d={`M${8 + i * 6} 58 A${42 - i * 6} ${42 - i * 6} 0 0 1 ${92 - i * 6} 58`}
          fill="none"
          stroke={c}
          strokeWidth="6"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

/** ROYGBIV-ish ramp used for wordmark gradient, holo rings, channel edges. */
export const RAINBOW = ["#FF2D9B", "#FF861E", "#FFD400", "#10E0B4", "#2D7BFF", "#9B2BFF"];
